import React, { Fragment } from 'react';

import { reorderActiveLayers } from '../../redux/active_layers/Actions';
import { IActiveLayer } from '../../redux/active_layers/activeLayerTypes';
import { ILayer, ILegendItem } from '../../redux/layers/layerTypes';
import { setRefreshMap } from '../../redux/map/Actions';
import { MAIN_MAP } from '../../redux/map/Constants';
import { useDispatch, useSelector } from 'react-redux';
import type { StoreState } from '../../redux/Store';

import { makeStyles, createStyles } from '@material-ui/styles';
import { Theme } from '@material-ui/core';

import SiteCardLegendItem from './SiteCardLegendItem.jsx';

import {
    DragDropContext,
    Droppable,
    Draggable,
    DroppableProps,
    DropResult
} from 'react-beautiful-dnd';

import LegendItem from './LegendItem';
import DrawnSketchesLegendItem from './DrawnSketchesLegendItem';

function Legend() {
    const classes = useStyles();
    // const settings = useSelector((state: StoreState) => state.layerSettingsReducer.find(setting => setting.uid === layer.system_name));
    const activeLayers: IActiveLayer[] = useSelector((state: StoreState) => state.activeLayers);
    const layers: ILayer[] = useSelector((state: StoreState) => state.layers.layers);
    const layerSettings = useSelector((state: StoreState) => state.layerSettingsReducer);
    const baseLayer = useSelector((state: StoreState) => state.layers.baseLayer);
    const filters = useSelector((state: StoreState) => state.siteCard.filters);
    const dispatch = useDispatch();
    const dispatchReorderActiveLayers = (activeLayers: IActiveLayer[]): any => {
        dispatch(reorderActiveLayers(activeLayers));
    };

    const currentProject = useSelector(state => state.project.currentProject);

    const areSiteCardsVisible = useSelector(
        (state: StoreState) => state.siteCard.areSiteCardsVisible
    );

    const dispatchSetRefreshMap = (value: boolean): any => {
        dispatch(setRefreshMap(value, MAIN_MAP));
    };

    const onDragEnd = (result: DropResult): any => {
        const { destination, source, draggableId } = result;
        if (!destination) {
            return;
        }

        if (destination.droppableId === source.droppableId && destination.index === source.index) {
            return;
        }

        const newActiveLayers = [...activeLayers.reverse()];

        newActiveLayers.splice(destination.index, 0, newActiveLayers.splice(source.index, 1)[0]);

        dispatchReorderActiveLayers(newActiveLayers.reverse());
        dispatchSetRefreshMap(true);
    };

    const selectedLayers = activeLayers
        .map(activeLayer => {
            const layer = layers.find(layer => layer.system_name === activeLayer.uid);
            return {
                ...activeLayer,
                ...(layers.find(layer => layer.system_name === activeLayer.uid) ?? {}),
                ...(layerSettings.find(settings => settings.uid === activeLayer.uid) ?? {}),
                userDrawing: !!activeLayer.userDrawing // Convert to boolean
            };
        })
        .reverse() as ILegendItem[];

    return selectedLayers.length > 0 || areSiteCardsVisible ? (
        <div className={`${classes.root}`}>
            {areSiteCardsVisible && <SiteCardLegendItem project={currentProject} isStatic />}
            <DragDropContext onDragEnd={onDragEnd}>
                <Droppable droppableId="legend">
                    {(droppableProvided): any => (
                        <div
                            className={classes.scrollableContainer}
                            {...droppableProvided.droppableProps}
                            ref={droppableProvided.innerRef}>
                            {selectedLayers.map((layer, idx) => (
                                <Draggable key={layer.uid} draggableId={layer.uid} index={idx}>
                                    {(draggableProvided): any => (
                                        <div
                                            {...draggableProvided.draggableProps}
                                            {...draggableProvided.dragHandleProps}
                                            ref={draggableProvided.innerRef}>
                                            {layer.userDrawing ? (
                                                <DrawnSketchesLegendItem />
                                            ) : (
                                                <LegendItem layer={layer} />
                                            )}
                                        </div>
                                    )}
                                </Draggable>
                            ))}
                            {droppableProvided.placeholder}
                        </div>
                    )}
                </Droppable>
            </DragDropContext>
        </div>
    ) : null;
}

const useStyles = makeStyles((theme: Theme) =>
    createStyles({
        root: {
            background: theme.palette.background.main,
            boxSizing: 'border-box',
            transition: '0.3s ease-out',
            pointerEvents: 'all'
        },
        scrollableContainer: {
            display: 'flex',
            flexDirection: 'column',
            margin: '10px 0',
            '&> *:not(:last-child)': {
                marginBottom: 10
            }
        }
    })
);

export default Legend;
